/**
 * `lending`'s shared vocabulary: the bank's own stage labels, the submission
 * labels, and the shape of a Hakili filter outcome.
 *
 * **Two vocabularies coexist and must never be conflated** (05, 2.6):
 * `SUBMISSION_LABELS` is what HAKILI sees of a bank, four values; and
 * `BANK_FILE_STAGE_LABELS` is the bank's OWN internal workflow, eleven values.
 * The console's `soumis` stage covers the whole of the second one, seen from
 * outside. They are two maps here for the same reason they are two enums in the
 * schema: one name for both is how a console starts showing a client a bank's
 * internal state.
 *
 * **Labels here, numbers in configuration tables**, which is 9.2's line and the
 * reason `POIDS_ETAPE_BANQUE` is absent: the weights live in
 * `lending.bank_file_stage_definitions` because a founder tunes them without a
 * deployment, and a copy compiled into six frontends drifts the first time one
 * is tuned.
 *
 * **The filter's outcome types live here** because two boundary elements need
 * them and neither may read the other: `lending/api` publishes
 * `listEligibleBanks` and `lending/domain` evaluates it, and 9.3.1 lets
 * `module-api` import another module's api, `platform` and `contracts` and
 * nothing else. Round 176 settled that pattern for `expertise`'s verdict.
 */

import type { FilterCause } from "./filter-causes.js";

/** 02, 2.4.7's four values: what HAKILI sees of a bank. */
export const SUBMISSION_STATUSES = ["etude", "retour", "offre", "refus"] as const;
export type SubmissionStatusCode = (typeof SUBMISSION_STATUSES)[number];

export const SUBMISSION_LABELS: Readonly<Record<SubmissionStatusCode, string>> = {
  etude: "En étude",
  retour: "Retour pour complément",
  offre: "Offre transmise",
  refus: "Refus",
};

/** 02, 2.4.3's eleven values: the bank's OWN internal workflow. */
export const BANK_FILE_STAGES = [
  "nouveau",
  "revue",
  "validation",
  "offre",
  "comite",
  "signature",
  "conditions",
  "decaisse",
  "retour",
  "refuse",
  "perdu",
] as const;
export type BankFileStageCode = (typeof BANK_FILE_STAGES)[number];

/**
 * The labels the bank console renders , **one vocabulary per stage, the
 * drawing's** (`hakili-bank.jsx`, `dealStatusStyles`), since round `L-1`'s
 * `M0` (15 September 2026, the architect's ruling 3 on `L-0`): the migration
 * `20260915230000_bank_vocabulary_of_the_drawing` corrects the eleven rows of
 * `lending.bank_file_stage_definitions` to these words, and the screens read
 * the door's label again. `L-0` had carried the drawing's words BESIDE the
 * seed's as `BANK_FILE_STAGE_CHIPS`; that constant is retired.
 *
 * 05, 2.6.6 gives the seed of `bank_file_stage_definitions` its weights in
 * full and names the sensitive stages; it does not give the French labels, and
 * 02, 2.4.3 gives only the codes of `stageFlow` plus the word *"Décaissé"* in
 * the dossier stage table. The eleven are therefore the drawing's.
 */
export const BANK_FILE_STAGE_LABELS: Readonly<Record<BankFileStageCode, string>> = {
  nouveau: "Nouveau",
  revue: "En revue",
  validation: "En validation",
  offre: "Offre émise",
  comite: "Au comité",
  signature: "Signature",
  conditions: "Conditions préalables",
  decaisse: "Décaissé",
  retour: "Retourné à Hakili",
  refuse: "Refusé",
  perdu: "Offre déclinée",
};

/**
 * **The applied migration's own words, frozen.** `20260829010000_init_lending`
 * seeded `bank_file_stage_definitions` with these eleven labels through its
 * assembler (`lending/infra/sql/init-lending.ts`), and an applied migration is
 * immutable: CI reads the assembler's output against the file on disk. The
 * assembler therefore reads THIS constant and nothing else does , it is not a
 * vocabulary in use, it is the record of what was written on 29 August.
 * `hakili/no-literal-label` sends a stage label here and nowhere else.
 */
export const BANK_FILE_STAGE_LABELS_AS_SEEDED_20260829: Readonly<Record<BankFileStageCode, string>> = {
  nouveau: "Nouveau",
  revue: "En revue",
  validation: "Validation du chef",
  offre: "Offre",
  comite: "Comité de crédit",
  signature: "Signature",
  conditions: "Conditions préalables",
  decaisse: "Décaissé",
  retour: "Retour pour complément",
  refuse: "Refusé",
  perdu: "Perdu",
};

/** The stepper's eight words (`hakili-bank.jsx`, `stageLabels`): the drawing's, shorter than the chips'. */
export const BANK_FILE_STEP_LABELS: Readonly<
  Record<Exclude<BankFileStageCode, "retour" | "refuse" | "perdu">, string>
> = {
  nouveau: "Nouveau",
  revue: "Revue",
  validation: "Validation",
  offre: "Offre",
  comite: "Comité",
  signature: "Signature",
  conditions: "Conditions",
  decaisse: "Décaissé",
};

/** 03, WF-BNK-05's three sub-steps, and the counter it shows. */
export const REVIEW_GROUPS = ["pieces", "coherence", "conformite"] as const;
export type ReviewGroupCode = (typeof REVIEW_GROUPS)[number];

export const REVIEW_GROUP_LABELS: Readonly<Record<ReviewGroupCode, string>> = {
  pieces: "Pièces",
  coherence: "Cohérence",
  conformite: "Conformité",
};

/** WF-CON-11's per-bank outcome, as the console renders it. */
export interface BankFilterOutcome {
  /** `FILTER-VERDICT-CAUSES`: the facts behind `reason` (codes and parameters), in the order the criteria were walked; absent on a retained or an inactive bank. */
  readonly causes?: readonly FilterCause[];
  readonly bankId: string;
  readonly name: string;
  readonly retained: boolean;
  /** WF-CON-11's own sentence: why this bank was kept or set aside. */
  readonly reason: string;
  /**
   * Which threshold source decided. Null when none did. `parametrage` is the
   * bank's CONFIGURED threshold (05 §2.12.5's `configuredThreshold`), for the
   * quotité and for the three criteria `OD-43` added to the filter alike.
   */
  readonly source: "prefiltre" | "parametrage" | null;
  /** `OD-43`: every criterion the bank failed, in the order walked; empty on a retained bank. */
  readonly failedCriteria: readonly FilterCriterion[];
  /**
   * `PRET-BANKS`: every criterion the bank leaves blank, in the order walked. A blank one excludes nothing, but a bank
   * with no quotité ceiling cannot be sent a file (its bank file derives its capacity from that ceiling).
   */
  readonly missingCriteria: readonly FilterCriterion[];
  /**
   * `NO-POLICY-GATE`: why a bank in `outOfPanel` was not considered , `inactive` (not on the panel) or `noPolicy` (on the panel, but its chef
   * has not saved a credit policy: « not sent at all »). Absent on a retained or excluded bank.
   */
  readonly outOfPanelFor?: "inactive" | "noPolicy";
}

/** The four criteria the filter walks: the quotité, the duration, the LTV, the vehicle's age. */
export type FilterCriterion = "quotite" | "duree" | "ltv" | "ageVehicule";

export interface HakiliFilterOutcome {
  readonly quotitePct: number;
  readonly retained: readonly BankFilterOutcome[];
  readonly excluded: readonly BankFilterOutcome[];
  /**
   * `PRET-BANKS` (A47): the banks NOT on the panel (`lending.banks.active = false`). They are neither retained nor excluded
   * , the filter did not consider them , but a screen that lists the panel must be able to say they exist and why they
   * are not offered. Never counted in `passed`.
   */
  readonly outOfPanel: readonly BankFilterOutcome[];
  /** WF-CON-11, step 4: a passing verdict is at least one eligible bank. */
  readonly passed: boolean;
}

/**
 * `round C2`, `C2.1`, moved here in `C2.9`: **the three employment situations
 * `02` §2.6.3 names, which the prequalification door requires.**
 *
 * They are WIRE VALUES before they are labels: `POST /api/v1/prequalifications`
 * refuses anything but these three with « La situation doit être précisée », so
 * a list that drifted by one accent would put that refusal back with nobody
 * able to see why. They live here and not in the screen for the reason
 * `no-french-outside-catalogue` gives: an accented string in an app is a label
 * that belongs in the catalogue. This one is not a label the copy may re-word
 * , it is the door's own vocabulary , and `SUBMISSION_LABELS` above is the
 * precedent for a French value map living in this package.
 */
export const EMPLOYMENT_SITUATIONS = ["Fonctionnaire", "Salarié privé", "Autre"] as const;

export type EmploymentSituation = (typeof EMPLOYMENT_SITUATIONS)[number];

/**
 * The testid suffix per situation , ASCII, and derived from nothing.
 *
 * `09-01`: every new NAME is English and machine-readable. Building a selector
 * from the French value would put an accent and a space into it, and a value
 * the door may one day spell differently would silently rename a test hook.
 */
export const EMPLOYMENT_SITUATION_KEYS: Readonly<Record<EmploymentSituation, string>> = {
  Fonctionnaire: "public",
  "Salarié privé": "private",
  Autre: "other",
};
